import { useCallback, useMemo } from 'react';
import {
    useSeksjoner,
    useSaveSeksjon,
    useDeleteSeksjon,
    useToggleSeksjonOverride,
    useHideInheritedSeksjon,
    useUnhideInheritedSeksjon,
    useReorderSeksjoner,
    resolveOmrådeAncestors,
    resolveDelområdeAncestors,
} from '@origo/seksjon';
import type { domain } from '@origo/seksjon';
import type { Område } from '@origo/seksjon';
import type {
    Seksjon,
    SeksjonContext,
    SeksjonFormValues,
    SeksjonsType as AdminSeksjonsType,
} from '../types';

/**
 * Admin-facing hook wrapping @origo/seksjon's query + mutation hooks.
 *
 * This is the only integration point between the admin app and the new
 * service-layer. It keeps the admin-local `Seksjon` shape so all components
 * (SectionItem, SectionList, NavPanel, …) stay untouched. Adapter
 * `toAdminSeksjon` converts domain.AdminSeksjon → admin-local Seksjon.
 */

const CONTEXT_TYPE_MAP: Record<
    Exclude<AdminSeksjonsType, 'delomrade-admin' | 'egendefinerte-admin' | 'innstillinger'>,
    domain.SeksjonsKontekst['type']
> = {
    kommune: 'Kommune',
    plan: 'Plan',
    delomradetype: 'Delområdetype',
    delomradegruppe: 'Delområdegruppe',
    delomrade: 'Delområde',
    omrade: 'Område',
};

function toDomainContext(ctx: SeksjonContext): domain.SeksjonsKontekst | null {
    switch (ctx.type) {
        case 'kommune':
            return { type: 'Kommune' };
        case 'plan':
            return { type: 'Plan', planId: ctx.planId };
        case 'delomradetype':
            if (!ctx.delomradetype) return null;
            return {
                type: 'Delområdetype',
                planId: ctx.planId,
                delområdetype: ctx.delomradetype,
            };
        case 'delomradegruppe':
            if (!ctx.gruppeId || !ctx.delomradetype) return null;
            return {
                type: 'Delområdegruppe',
                planId: ctx.planId,
                gruppeId: ctx.gruppeId,
                delområdetype: ctx.delomradetype,
            };
        case 'delomrade':
            if (!ctx.omradeId) return null;
            return {
                type: 'Delområde',
                planId: ctx.planId,
                områdeId: ctx.omradeId,
                delområdetype: ctx.delomradetype,
            };
        case 'omrade':
            if (!ctx.omradeId) return null;
            return {
                type: 'Område',
                planId: ctx.planId,
                områdeId: ctx.omradeId,
            };
        case 'delomrade-admin':
            // Ingen seksjons-kontekst — AdminPage rendrer DelomradeAdmin
            // i stedet for SectionList når dette er aktiv kontekst.
            return null;
        case 'egendefinerte-admin':
            // Ingen seksjons-kontekst — AdminPage rendrer EgendefinerteOmraderAdmin
            // i stedet for SectionList når dette er aktiv kontekst.
            return null;
        case 'innstillinger':
            return null;
    }
}

/**
 * Backwards-compat alias for eksisterende call-sites som importerer
 * `SeksjonValues`. Foretrukket nytt navn er `SeksjonFormValues` (re-eksporteres
 * fra types.ts).
 */
export type SeksjonValues = SeksjonFormValues;

export interface UseAdminSectionsReturn {
    seksjoner: Seksjon[];
    loading: boolean;
    addSeksjon: (values: SeksjonFormValues, options?: { synlig?: boolean; prioritet?: number }) => Promise<string>;
    updateSeksjon: (id: string, values: SeksjonFormValues) => Promise<void>;
    deleteSeksjon: (id: string) => Promise<void>;
    reorder: (newOrder: string[]) => Promise<void>;
    toggleOverride: (
        globalId: string,
        isInherited: boolean,
        override: boolean
    ) => Promise<void>;
    hideSection: (globalId: string) => Promise<void>;
    unhideSection: (hideObjectId: number) => Promise<void>;
}

export interface UseAdminSectionsOptions {
    /** Egendefinerte områder for planen — brukes for Område-kontekst arvekjede. */
    egendefinerte?: Område[];
    /** FKB delområde-tre — brukes for Område-kontekst arv gjennom Delområde-forelder. */
    areas?: Område[];
    /** Kalles etter vellykket lagring, sletting eller rekkefølgeendring. */
    onEdited?: (seksjonCountDelta?: number) => void;
}

export function useAdminSections(
    ctx: SeksjonContext,
    options: UseAdminSectionsOptions = {}
): UseAdminSectionsReturn {
    const { egendefinerte = [], areas = [], onEdited } = options;

    const domainContext = useMemo(() => toDomainContext(ctx), [ctx]);

    // Pre-resolve ancestry client-side — the use-case accepts it as input
    // and skips the repository lookup when supplied. Saves a round-trip since
    // admin already has begge lister lastet (egendefinerte + areas).
    //
    // - Område: full polymorf parent-kjede.
    // - Delområde: kun gruppe-medlemskap (delomradegruppe-member-rader i
    //   `egendefinerte`-listen — ingen ekstra fetch trengs).
    const ancestry = useMemo(() => {
        if (domainContext?.type === 'Område' && ctx.omradeId) {
            return resolveOmrådeAncestors(ctx.omradeId, ctx.planId, egendefinerte, areas);
        }
        if (domainContext?.type === 'Delområde' && ctx.omradeId) {
            return resolveDelområdeAncestors(ctx.omradeId, ctx.planId, egendefinerte, areas);
        }
        return undefined;
    }, [domainContext, ctx, egendefinerte, areas]);

    const { data, isLoading } = useSeksjoner(domainContext, {
        ancestry,
    });

    const saveMutation = useSaveSeksjon();
    const deleteMutation = useDeleteSeksjon();
    const toggleMutation = useToggleSeksjonOverride();
    const hideMutation = useHideInheritedSeksjon();
    const unhideMutation = useUnhideInheritedSeksjon();
    const reorderMutation = useReorderSeksjoner();

    // Seksjoner returneres direkte som `AdminSeksjon[]` fra domain — ingen
    // adapter. Komponentene leser `objectId`, `synlig`, `tittel`, `tekst`,
    // `isInherited` osv. direkte fra domenemodellen.
    const seksjoner: Seksjon[] = data ?? [];
    const currentSections = seksjoner;

    const addSeksjon = useCallback(
        async (values: SeksjonValues, options?: { synlig?: boolean; prioritet?: number }): Promise<string> => {
            if (!domainContext) throw new Error('Kontekst mangler');
            if (ctx.type === 'delomrade-admin' || ctx.type === 'egendefinerte-admin' || ctx.type === 'innstillinger') {
                throw new Error('Kan ikke legge til seksjon i admin-kontekst');
            }
            const seksjonstype = CONTEXT_TYPE_MAP[ctx.type];
            const isKommune = ctx.type === 'kommune';
            const områdeId = ctx.type === 'delomradegruppe' ? ctx.gruppeId : ctx.omradeId;
            const result = await saveMutation.mutateAsync({
                values: {
                    seksjonstype,
                    planId: isKommune ? undefined : ctx.planId,
                    områdeId,
                    delområdetype: ctx.delomradetype,
                    områdeNavn: ctx.omradeNavn,
                    tittel: values.tittel,
                    tekst: values.tekst,
                    synlig: options?.synlig ?? true,
                    kommunal: isKommune || values.kommunal,
                    infoboks: Boolean(values.infoboks),
                    topp: values.topp,
                    prioritet: options?.prioritet ?? currentSections.length,
                    overstyrer: false,
                },
            });
            onEdited?.(1);
            return result.globalId;
        },
        [domainContext, ctx, saveMutation, currentSections.length, onEdited]
    );

    const updateSeksjon = useCallback(
        async (id: string, values: SeksjonValues) => {
            const objectId = Number(id);
            const existing = currentSections.find((s) => s.objectId === objectId);
            if (!existing) throw new Error(`Fant ikke seksjon med id=${id}`);
            await saveMutation.mutateAsync({
                values: {
                    ...existing,
                    tittel: values.tittel,
                    tekst: values.tekst,
                    topp: values.topp,
                    infoboks: Boolean(values.infoboks),
                    ...(values.synlig !== undefined ? { synlig: values.synlig } : {}),
                },
                editing: existing,
            });
            onEdited?.();
        },
        [currentSections, saveMutation, onEdited]
    );

    const deleteSeksjon = useCallback(
        async (id: string) => {
            const objectId = Number(id);
            await deleteMutation.mutateAsync({ objectId });
            onEdited?.(-1);
        },
        [deleteMutation, onEdited]
    );

    const reorder = useCallback(
        async (newOrder: string[]) => {
            if (!data) return;
            const byId = new Map(data.map((s) => [String(s.objectId), s]));
            // `normalizePriorities` sammenligner `prioritet`-feltet (NÅVÆRENDE
            // verdi) med posisjonen i arrayet (ØNSKET ny verdi) — bare rader
            // hvor disse avviker blir oppdatert. Vi må derfor bevare den
            // eksisterende prioriteten, ikke skrive over den med idx.
            const ordered = newOrder
                .map((id) => byId.get(id))
                .filter((s): s is domain.AdminSeksjon => !!s)
                .map((s) => ({ objectId: s.objectId, prioritet: s.prioritet }));
            await reorderMutation.mutateAsync({ ordered });
            onEdited?.();
        },
        [data, reorderMutation, onEdited]
    );

    const toggleOverride = useCallback(
        async (globalId: string, _isInherited: boolean, override: boolean) => {
            if (!domainContext) throw new Error('Kontekst mangler');
            await toggleMutation.mutateAsync({
                sourceGlobalId: globalId,
                enable: override,
                context: domainContext,
            });
        },
        [domainContext, toggleMutation]
    );

    const hideSection = useCallback(
        async (globalId: string) => {
            if (!domainContext) throw new Error('Kontekst mangler');
            await hideMutation.mutateAsync({
                sourceGlobalId: globalId,
                context: domainContext,
            });
            onEdited?.();
        },
        [domainContext, hideMutation, onEdited]
    );

    const unhideSection = useCallback(
        async (hideObjectId: number) => {
            await unhideMutation.mutateAsync({ hideObjectId });
            onEdited?.();
        },
        [unhideMutation, onEdited]
    );

    return {
        seksjoner,
        loading: isLoading,
        addSeksjon,
        updateSeksjon,
        deleteSeksjon,
        reorder,
        toggleOverride,
        hideSection,
        unhideSection,
    };
}
